import React, { useState, useEffect } from 'react';
import { Settings, Users, CreditCard, Package, Save, Filter } from 'lucide-react';
import stripeApiService from '../services/stripeApiService';
import stripeProductsConfigService from '../services/stripeProductsConfigService';

/**
 * Composant pour configurer l'usage des produits Stripe
 * Permet de choisir quels produits utiliser pour les abonnements et la facturation
 */
const StripeProductsConfigurator = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [config, setConfig] = useState({
    subscriptionProducts: [], // IDs des produits pour abonnements
    invoiceProducts: [] // IDs des produits pour facturation
  });
  const [filters, setFilters] = useState({
    category: 'all',
    target: 'all',
    active: true
  });

  // Charger les produits Stripe et la configuration
  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [productsWithPrices, existingConfig] = await Promise.all([
        stripeApiService.getProductsWithPrices(),
        stripeProductsConfigService.getProductsConfig()
      ]);
      
      setProducts(productsWithPrices);
      
      // Charger la configuration existante depuis Firestore
      setConfig({
        subscriptionProducts: existingConfig.subscriptionProducts || [],
        invoiceProducts: existingConfig.invoiceProducts || []
      });
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError(`Erreur lors du chargement: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Filtrer les produits
  const filteredProducts = products.filter(product => {
    if (filters.category !== 'all' && product.metadata?.category !== filters.category) {
      return false;
    }
    if (filters.target !== 'all' && product.metadata?.target !== filters.target) {
      return false;
    }
    if (filters.active && !product.active) {
      return false;
    }
    return true;
  });

  // Toggle un produit dans une catégorie
  const toggleProductUsage = (productId, usageType) => {
    setConfig(prev => {
      const currentArray = prev[usageType];
      const isSelected = currentArray.includes(productId);
      
      if (isSelected) {
        return {
          ...prev,
          [usageType]: currentArray.filter(id => id !== productId)
        };
      } else {
        return {
          ...prev,
          [usageType]: [...currentArray, productId]
        };
      }
    });
  };

  // Sauvegarder la configuration
  const saveConfiguration = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      await stripeProductsConfigService.saveProductsConfig(config);
      
      setMessage({ type: 'success', text: 'Configuration sauvegardée avec succès' });
      
      // Effacer le message après 3 secondes
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError(`Erreur lors de la sauvegarde: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Obtenir l'icône pour la catégorie
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'subscription': return CreditCard;
      case 'invoice': return Users;
      case 'service': return Package;
      default: return Package;
    }
  };

  // Obtenir la couleur pour la catégorie
  const getCategoryColor = (category) => {
    switch (category) {
      case 'subscription': return 'blue';
      case 'invoice': return 'green';
      case 'service': return 'purple';
      default: return 'gray';
    }
  };

  // Obtenir le label pour la cible
  const getTargetLabel = (target) => {
    switch (target) {
      case 'employee': return 'Intervenant';
      case 'beneficiary': return 'Bénéficiaire';
      default: return target;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
          <span className="text-gray-600 dark:text-gray-400">Chargement des produits...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Settings className="w-8 h-8 text-purple-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Configuration des produits
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Choisissez quels produits utiliser pour les abonnements et la facturation
            </p>
          </div>
        </div>
        <button
          onClick={saveConfiguration}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Sauvegarder
        </button>
      </div>

      {/* Messages */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-3">
          <Filter className="w-4 h-4 text-gray-500 mr-2" />
          <h3 className="font-medium text-gray-900 dark:text-white">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Catégorie
            </label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-900 dark:text-white rounded-md"
            >
              <option value="all">Toutes les catégories</option>
              <option value="subscription">Abonnements</option>
              <option value="invoice">Facturation</option>
              <option value="service">Services</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Public cible
            </label>
            <select
              value={filters.target}
              onChange={(e) => setFilters({ ...filters, target: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-900 dark:text-white rounded-md"
            >
              <option value="all">Tous les publics</option>
              <option value="employee">Intervenants</option>
              <option value="beneficiary">Bénéficiaires</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active-only"
              checked={filters.active}
              onChange={(e) => setFilters({ ...filters, active: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="active-only" className="text-sm text-gray-700 dark:text-gray-300">
              Produits actifs uniquement
            </label>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Produits pour abonnements */}
        <div className="border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <CreditCard className="w-5 h-5 text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              Produits pour Abonnements
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Ces produits seront affichés dans la page d'abonnement pour les intervenants
          </p>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredProducts
              .filter(product => product.metadata?.category === 'subscription' || filters.category !== 'all')
              .map(product => {
                const CategoryIcon = getCategoryIcon(product.metadata?.category);
                const categoryColor = getCategoryColor(product.metadata?.category);
                const isSelected = config.subscriptionProducts.includes(product.id);

                return (
                  <div
                    key={product.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => toggleProductUsage(product.id, 'subscriptionProducts')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <CategoryIcon className={`w-4 h-4 mr-2 text-${categoryColor}-500`} />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                <div className="flex items-center space-x-2">
                                  <span className={`inline-block px-1.5 py-0.5 text-xs rounded ${
                                    product.metadata?.category === 'subscription' 
                                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                      : product.metadata?.category === 'invoice'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                      : product.metadata?.category === 'service'
                                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                  }`}>
                                    {product.metadata?.category || 'Non catégorisé'}
                                  </span>
                                  <span className={`inline-block px-1.5 py-0.5 text-xs rounded ${
                                    product.metadata?.target === 'employee'
                                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
                                      : product.metadata?.target === 'beneficiary'
                                      ? 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300'
                                      : product.metadata?.target === 'both'
                                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                  }`}>
                                    {getTargetLabel(product.metadata?.target) || 'Cible non définie'}
                                  </span>
                                </div>
                                {product.metadata?.features && (
                                  <div className="text-gray-600 dark:text-gray-400">
                                    Niveau: {product.metadata.features}
                                  </div>
                                )}
                                <div className="text-gray-500 dark:text-gray-500">
                                  {product.prices.length} prix configuré(s)
                                </div>
                              </div>
                            </div>
                        </div>
                      </div>
                      <div className={`px-2 py-1 text-xs rounded-full ${
                        product.active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {product.active ? 'Actif' : 'Inactif'}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Produits pour facturation */}
        <div className="border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-green-700 dark:text-green-300">
              Produits pour Facturation
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Ces produits seront utilisés pour facturer les bénéficiaires/clients
          </p>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredProducts
              .filter(product => product.metadata?.category === 'invoice' || filters.category !== 'all')
              .map(product => {
                const CategoryIcon = getCategoryIcon(product.metadata?.category);
                const categoryColor = getCategoryColor(product.metadata?.category);
                const isSelected = config.invoiceProducts.includes(product.id);

                return (
                  <div
                    key={product.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-600'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => toggleProductUsage(product.id, 'invoiceProducts')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <CategoryIcon className={`w-4 h-4 mr-2 text-${categoryColor}-500`} />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {product.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-block px-1.5 py-0.5 text-xs rounded ${
                                  product.metadata?.category === 'subscription' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                    : product.metadata?.category === 'invoice'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                    : product.metadata?.category === 'service'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                }`}>
                                  {product.metadata?.category || 'Non catégorisé'}
                                </span>
                                <span className={`inline-block px-1.5 py-0.5 text-xs rounded ${
                                  product.metadata?.target === 'employee'
                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
                                    : product.metadata?.target === 'beneficiary'
                                    ? 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300'
                                    : product.metadata?.target === 'both'
                                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                }`}>
                                  {getTargetLabel(product.metadata?.target) || 'Cible non définie'}
                                </span>
                              </div>
                              {product.metadata?.features && (
                                <div className="text-gray-600 dark:text-gray-400">
                                  Niveau: {product.metadata.features}
                                </div>
                              )}
                              <div className="text-gray-500 dark:text-gray-500">
                                {product.prices.length} prix configuré(s)
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={`px-2 py-1 text-xs rounded-full ${
                        product.active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {product.active ? 'Actif' : 'Inactif'}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
        <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">
          Résumé de la configuration
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-purple-700 dark:text-purple-300 font-medium">
              Produits pour abonnements :
            </span>
            <span className="ml-2 text-purple-600 dark:text-purple-400">
              {config.subscriptionProducts.length} sélectionné(s)
            </span>
          </div>
          <div>
            <span className="text-purple-700 dark:text-purple-300 font-medium">
              Produits pour facturation :
            </span>
            <span className="ml-2 text-purple-600 dark:text-purple-400">
              {config.invoiceProducts.length} sélectionné(s)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeProductsConfigurator;
